/**
 * Global Error Boundary Component
 * Catches and handles JavaScript errors in the React component tree
 */

import React from 'react';
import type { ErrorBoundaryState, ErrorInfo } from '../../types';
import { DefaultErrorFallback } from '../ui/ErrorFallback';
import { config, isDevelopment } from '../../utils/config';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error: Error; reset: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Error Boundary caught an error:', error, errorInfo);

        // Log error to external service in production
        if (!isDevelopment) {
            this.logErrorToService(error, errorInfo);
        }
    }

    private logErrorToService = async (error: Error, errorInfo: ErrorInfo) => {
        try {
            // In a real app, you would send this to your error tracking service
            // For example: Sentry, LogRocket, Bugsnag, etc.
            await fetch(`${config.API_BASE_URL}/errors`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: error.message,
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString(),
                }),
            });
        } catch (logError) {
            console.error('Failed to log error to service:', logError);
        }
    };

    private handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    render() {
        if (this.state.hasError) {
            // If a custom fallback component is provided, use it
            if (this.props.fallback) {
                const FallbackComponent = this.props.fallback;
                return <FallbackComponent error={this.state.error!} reset={this.handleReset} />;
            }

            // Default error UI
            return <DefaultErrorFallback error={this.state.error!} reset={this.handleReset} />;
        }

        return this.props.children;
    }
}